import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { useVendors, usePlants } from "../../lib/useReference";
import { formatDate } from "../../lib/dateFormat";
import { useDebounce } from "../../lib/useDebounce";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { FieldSelect, FieldInput } from "../../components/ui/Field";
import { ReportTable, type ReportColumn } from "../../components/ui/ReportTable";

interface PoAuditLogRow {
  id: number;
  poCreatedDate: string;
  poNumber: string;
  poLineItem: number;
  abLineSequence: number | null;
  statusName: string;
  materialNumber: string;
  materialDescription: string;
  changeSetNo: string | number | null;
  originalQty: number;
  proposedQty: number | null;
  originalDeliveryDate: string;
  proposedDeliveryDate: string | null;
  plantId: number;
  vendorId: number;
  logCreatedDate: string;
  loggedInUser: string | null;
}

// No user-facing Date Format control for the audit log (not part of this
// screen's documented filters) — dates still render nicely via a fixed format.
const DATE_FORMAT = "DD/MMM/YYYY";

/** PO Audit Log — SBD-only, read-only trail of every change-set applied to a
 * PO line (server-side access gate already enforces the SBD-only rule). */
export function PoAuditLog() {
  const [vendorId, setVendorId] = useState("");
  const [plantId, setPlantId] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();

  const vendorMap = useMemo(() => new Map((vendors ?? []).map((v) => [v.id, v.vendorNumber])), [vendors]);
  const plantMap = useMemo(() => new Map((plants ?? []).map((p) => [p.id, p.plantNumber])), [plants]);

  const materialNumberDebounced = useDebounce(materialNumber);

  const query = useQuery({
    queryKey: ["po-audit-log", vendorId, plantId, materialNumberDebounced, fromDate, toDate],
    queryFn: () =>
      api.get<{ rows: PoAuditLogRow[] }>(
        `/po/audit-log?${new URLSearchParams({
          ...(vendorId && { vendorId }),
          ...(plantId && { plantId }),
          ...(materialNumberDebounced && { materialNumber: materialNumberDebounced }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
        })}`
      ),
  });

  const columns = useMemo<ReportColumn<PoAuditLogRow>[]>(
    () => [
      { key: "poCreatedDate", label: "PO Created Date", render: (r) => formatDate(r.poCreatedDate, DATE_FORMAT) },
      { key: "poNumber", label: "PO Number" },
      { key: "poLineItem", label: "PO Line", align: "right" },
      { key: "abLineSequence", label: "AB Line Seq.", align: "right" },
      { key: "statusName", label: "Status" },
      { key: "materialNumber", label: "Material" },
      { key: "materialDescription", label: "Description" },
      { key: "changeSetNo", label: "Change Set No" },
      { key: "originalQty", label: "Original Qty", align: "right" },
      { key: "proposedQty", label: "Proposed Qty", align: "right" },
      { key: "originalDeliveryDate", label: "Original Delivery Date", render: (r) => formatDate(r.originalDeliveryDate, DATE_FORMAT) },
      { key: "proposedDeliveryDate", label: "Proposed Delivery Date", render: (r) => formatDate(r.proposedDeliveryDate, DATE_FORMAT) },
      { key: "plantId", label: "Plant", render: (r) => plantMap.get(r.plantId) ?? r.plantId },
      { key: "vendorId", label: "Vendor", render: (r) => vendorMap.get(r.vendorId) ?? r.vendorId },
      { key: "logCreatedDate", label: "Log Created Date", render: (r) => formatDate(r.logCreatedDate, DATE_FORMAT) },
      { key: "loggedInUser", label: "Logged By" },
    ],
    [plantMap, vendorMap]
  );

  return (
    <div>
      <Card className="mb-5">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <FieldSelect
            label="Vendor Number"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            options={[{ value: "", label: "All vendors" }, ...(vendors ?? []).map((v) => ({ value: v.id, label: `${v.vendorNumber} - ${v.name}` }))]}
          />
          <FieldSelect
            label="Plant Number"
            value={plantId}
            onChange={(e) => setPlantId(e.target.value)}
            options={[{ value: "", label: "All plants" }, ...(plants ?? []).map((p) => ({ value: p.id, label: `${p.plantNumber} - ${p.name}` }))]}
          />
          <FieldInput label="Material Number" value={materialNumber} onChange={(e) => setMaterialNumber(e.target.value)} />
          <div>
            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Log Date Range</label>
            <div className="mt-1 flex gap-2">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="text-sm border border-slate-200 dark:border-white/10 rounded-lg px-2 py-2 bg-white dark:bg-white/5 w-1/2" />
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="text-sm border border-slate-200 dark:border-white/10 rounded-lg px-2 py-2 bg-white dark:bg-white/5 w-1/2" />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-4 flex items-center justify-between border-b border-slate-100 dark:border-white/10">
          <span className="font-semibold text-sm">PO Audit Log</span>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              title="Refresh"
              aria-label="Refresh"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 outline-none focus-visible:ring-2 focus-visible:ring-sbd-yellow/60 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
            </button>
            <Button variant="outline" icon={Download} onClick={() => api.download("/po/export/audit-log", "PO_Audit_Log.xlsx")}>
              Export to Excel
            </Button>
          </div>
        </div>
        <ReportTable columns={columns} rows={query.data?.rows ?? []} rowKey={(r) => r.id} loading={query.isLoading} emptyMessage="No audit log entries match this filter." />
      </Card>
    </div>
  );
}
